import { type ReactNode, createContext, useCallback, useState } from "react";

import { useAuthAppData } from "components/root/use-auth-app-data";
import type {
  CommonPolicyDataQuery,
  GetPolicyQuery,
  UserPolicyIDsQuery,
} from "gql/__generated__/hooks";
import { type Policy } from "gql/types";
import { trackEvent } from "utils";

export type AuthAppContextState = {
  selectedPolicyId: string;
  propertyAddress: Policy["propertyAddress"] | null;
  policy: Policy;
  policies: UserPolicyIDsQuery["userPolicies"];
  userBilling: CommonPolicyDataQuery["userBilling"] | null;
  userDetails: CommonPolicyDataQuery["userDetails"] | null;
  userDocuments: CommonPolicyDataQuery["userDocuments"] | null;
  userInfo: CommonPolicyDataQuery["userBasicInfo"] | null;
  userInsuranceRep: CommonPolicyDataQuery["userInsuranceRep"] | null;
  userPolicyProperties: CommonPolicyDataQuery["userPolicyProperties"] | null;
  isPolicyDataReady: boolean;
};

export type AuthAppContextInterface = {
  setPolicy: (policy: Policy) => void;
  setPolicies: (policies: UserPolicyIDsQuery["userPolicies"]) => void;
  setUserBilling: (userBilling: CommonPolicyDataQuery["userBilling"]) => void;
  setUserDetails: (userDetails: CommonPolicyDataQuery["userDetails"]) => void;
  setUserDocuments: (
    userDocuments: CommonPolicyDataQuery["userDocuments"]
  ) => void;
  setUserInfo: (userInfo: CommonPolicyDataQuery["userBasicInfo"]) => void;
  setUserInsuranceRep: (
    userInsuranceRep: CommonPolicyDataQuery["userInsuranceRep"]
  ) => void;
  setUserPolicyProperties: (
    userPolicyProperties: CommonPolicyDataQuery["userPolicyProperties"]
  ) => void;
  refetchPolicy: () => void;
  refetchPolicies: () => void;
};

const initialStateDefault: AuthAppContextState = {
  selectedPolicyId: "",
  propertyAddress: null,
  policy: { policyId: "", insightPolicyId: "" },
  policies: [],
  userBilling: null,
  userDetails: null,
  userDocuments: null,
  userInfo: null,
  userInsuranceRep: null,
  userPolicyProperties: null,
  isPolicyDataReady: false,
};

const initialDispatchDefault: AuthAppContextInterface = {
  setPolicy: (_policy: Policy) => null,
  setPolicies: (_policies: UserPolicyIDsQuery["userPolicies"]) => null,
  setUserBilling: (_userBilling: CommonPolicyDataQuery["userBilling"]) => null,
  setUserDetails: (_userDetails: CommonPolicyDataQuery["userDetails"]) => null,
  setUserDocuments: (_userDocuments: CommonPolicyDataQuery["userDocuments"]) =>
    null,
  setUserInfo: (_userInfo: CommonPolicyDataQuery["userBasicInfo"]) => null,
  setUserInsuranceRep: (
    _userInsuranceRep: CommonPolicyDataQuery["userInsuranceRep"]
  ) => null,
  setUserPolicyProperties: (
    _userPolicyProperties: CommonPolicyDataQuery["userPolicyProperties"]
  ) => null,
  refetchPolicy: () => null,
  refetchPolicies: () => null,
};
const AuthAppContext = createContext<AuthAppContextState>(initialStateDefault);
const AuthAppDispatchContext = createContext<AuthAppContextInterface>(
  initialDispatchDefault
);

// This is broken out into a hook mostly to make testing easier
export const useAuthAppInternals = (initialState = initialStateDefault) => {
  const [state, setState] = useState(initialState);

  const setPolicy = useCallback((policy: Policy) => {
    setState((prevState) => {
      trackEvent("Policy Selected", {
        policySequence: (prevState?.policies?.indexOf(policy) ?? 0) + 1,
      });

      return {
        ...prevState,
        selectedPolicyId: policy?.policyId ?? "",
        propertyAddress: policy.propertyAddress,
        policy,
      };
    });
  }, []);

  const setSelectedPolicy = useCallback(
    (selectedPolicy: GetPolicyQuery["getPolicy"]) => {
      setState((prevState) => ({ ...prevState, selectedPolicy }));
    },
    []
  );

  const setPolicies = useCallback(
    (policies: UserPolicyIDsQuery["userPolicies"]) => {
      setState((prevState) => ({ ...prevState, policies }));
    },
    []
  );

  const setUserBilling = useCallback(
    (userBilling: CommonPolicyDataQuery["userBilling"]) => {
      setState((prevState) => ({ ...prevState, userBilling }));
    },
    []
  );

  const setUserDetails = useCallback(
    (userDetails: CommonPolicyDataQuery["userDetails"]) => {
      setState((prevState) => ({ ...prevState, userDetails }));
    },
    []
  );

  const setUserDocuments = useCallback(
    (userDocuments: CommonPolicyDataQuery["userDocuments"]) => {
      setState((prevState) => ({ ...prevState, userDocuments }));
    },
    []
  );

  const setUserInfo = useCallback(
    (userInfo: CommonPolicyDataQuery["userBasicInfo"]) => {
      setState((prevState) => ({ ...prevState, userInfo }));
    },
    []
  );

  const setUserInsuranceRep = useCallback(
    (userInsuranceRep: CommonPolicyDataQuery["userInsuranceRep"]) => {
      setState((prevState) => ({ ...prevState, userInsuranceRep }));
    },
    []
  );

  const setUserPolicyProperties = useCallback(
    (userPolicyProperties: CommonPolicyDataQuery["userPolicyProperties"]) => {
      setState((prevState) => ({ ...prevState, userPolicyProperties }));
    },
    []
  );

  const setIsPolicyDataReady = useCallback((isPolicyDataReady: boolean) => {
    setState((prevState) => ({ ...prevState, isPolicyDataReady }));
  }, []);

  const dispatchMethods = {
    setPolicy,
    setPolicies,
    setSelectedPolicy,
    setUserBilling,
    setUserDetails,
    setUserDocuments,
    setUserInfo,
    setUserInsuranceRep,
    setUserPolicyProperties,
  };

  const { refetchPolicy, refetchPolicies } = useAuthAppData(
    state,
    dispatchMethods,
    setIsPolicyDataReady
  );

  return {
    state,
    dispatchMethods: { ...dispatchMethods, refetchPolicy, refetchPolicies },
  };
};

function AuthAppProvider({ children }: { children: ReactNode }) {
  const { state, dispatchMethods } = useAuthAppInternals();

  return (
    <AuthAppContext.Provider value={state}>
      <AuthAppDispatchContext.Provider value={dispatchMethods}>
        {children}
      </AuthAppDispatchContext.Provider>
    </AuthAppContext.Provider>
  );
}

export {
  AuthAppContext,
  AuthAppDispatchContext,
  AuthAppProvider,
  initialStateDefault,
  initialDispatchDefault,
};
